import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Download, Printer, X, ChevronRight, CheckCircle2, 
  Upload, Settings, Key, ShoppingCart, Globe, Plug,
  FileText, ExternalLink, Copy, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step images URLs
const stepImages = {
  1: 'https://static.prod-images.emergentagent.com/jobs/ad93ba23-3e63-4a1a-8ae2-a69e9bffb0dd/images/f6723b305f03a462c6f49ae99cefd5e42246749a52cdd48710c79ae0b39f2e89.png',
  2: 'https://static.prod-images.emergentagent.com/jobs/ad93ba23-3e63-4a1a-8ae2-a69e9bffb0dd/images/ea6812f847d3e8c5a31f7df0ae4649f11b1fa5568093e5cf41e224ad507aea9d.png',
  3: 'https://static.prod-images.emergentagent.com/jobs/ad93ba23-3e63-4a1a-8ae2-a69e9bffb0dd/images/6adb4467fee67dd5dadb78e51e289836d56c930fadd13a58a10a7488013ef229.png',
  4: 'https://static.prod-images.emergentagent.com/jobs/ad93ba23-3e63-4a1a-8ae2-a69e9bffb0dd/images/dd0c6da6aa371f9c4498537ca6558d00426eafd3c193dc8c62d47903530d6bad.png',
  5: 'https://static.prod-images.emergentagent.com/jobs/ad93ba23-3e63-4a1a-8ae2-a69e9bffb0dd/images/5453f983fdbed55ccc7c18432afbd4078bae6bd706475d10b55575812a55de62.png',
  6: 'https://static.prod-images.emergentagent.com/jobs/ad93ba23-3e63-4a1a-8ae2-a69e9bffb0dd/images/826bcf5fa4082db8f11410e41560b89fbb5c337d102e141ba0f8d9557ef72348.png'
};

// Guide content in both languages
const guideContent = {
  en: {
    title: 'WooCommerce Plugin Installation Guide',
    subtitle: 'Follow these steps to integrate PayHub with your WordPress/WooCommerce store',
    downloadPlugin: 'Download Plugin',
    printPdf: 'Print / Save PDF',
    close: 'Close',
    requirements: {
      title: 'Requirements',
      items: [
        { label: 'WordPress', value: '5.0 or higher (6.0+ recommended)' },
        { label: 'WooCommerce', value: '5.0 or higher (7.0+ recommended)' },
        { label: 'PHP', value: '7.4 or higher (8.0+ recommended)' },
        { label: 'SSL Certificate', value: 'Required (HTTPS)' }
      ]
    },
    steps: [
      {
        number: 1,
        title: 'Download the Plugin',
        description: 'Download the PayHub Gateway plugin file (payhub-gateway.zip) from your admin dashboard.',
        tips: ['Keep the ZIP file as-is, do not extract it', 'The file is approximately 12KB']
      },
      {
        number: 2,
        title: 'Upload to WordPress',
        description: 'In your WordPress admin panel, navigate to Plugins → Add New → Upload Plugin.',
        substeps: [
          'Log in to your WordPress admin (yoursite.com/wp-admin)',
          'Go to Plugins → Add New',
          'Click "Upload Plugin" button at the top',
          'Click "Choose File" and select the ZIP file',
          'Click "Install Now"'
        ]
      },
      {
        number: 3,
        title: 'Activate the Plugin',
        description: 'After installation, click "Activate Plugin" to enable PayHub Gateway.',
        tips: ['You will see a success message once activated', 'The plugin will appear in your installed plugins list']
      },
      {
        number: 4,
        title: 'Configure WooCommerce',
        description: 'Go to WooCommerce → Settings → Payments to configure the PayHub payment method.',
        substeps: [
          'Navigate to WooCommerce → Settings',
          'Click on the "Payments" tab',
          'Find "PayHub" in the payment methods list',
          'Click "Set up" or "Manage"'
        ]
      },
      {
        number: 5,
        title: 'Enter Your API Key',
        description: 'Enter your PayHub API key to connect your store.',
        substeps: [
          'Enable the payment method by checking "Enable PayHub"',
          'Enter a title (e.g., "Pay with PayHub")',
          'Enter a description for customers',
          'Paste your API Key (get it from Settings → API Keys)',
          'Set the API URL to: ' + API_URL + '/api/woocommerce',
          'Click "Save changes"'
        ],
        important: 'Your API key can be created in the Settings → API Keys section of your PayHub account.'
      },
      {
        number: 6,
        title: 'Test the Integration',
        description: 'Make a test purchase to ensure everything works correctly.',
        substeps: [
          'Add a product to your cart',
          'Proceed to checkout',
          'Select "Pay with PayHub" as payment method',
          'Complete the order',
          'Verify the payment appears in PayHub transactions'
        ],
        tips: ['Use your own account for testing', 'Check both buyer and seller dashboards']
      }
    ],
    troubleshooting: {
      title: 'Troubleshooting',
      items: [
        {
          problem: 'Plugin not appearing in payment methods',
          solution: 'Ensure WooCommerce is installed and activated. Try deactivating and reactivating the PayHub plugin.'
        },
        {
          problem: 'API connection error',
          solution: 'Verify your API key is correct and not revoked. Check that your site uses HTTPS.'
        },
        {
          problem: 'Payment fails at checkout',
          solution: 'Check that the customer has sufficient balance. Verify the API URL is correct.'
        }
      ]
    },
    support: {
      title: 'Need Help?',
      text: 'Contact our support team or check the Knowledge Base in your admin dashboard for more information.'
    }
  },
  fr: {
    title: 'Guide d\'Installation du Plugin WooCommerce',
    subtitle: 'Suivez ces étapes pour intégrer PayHub à votre boutique WordPress/WooCommerce',
    downloadPlugin: 'Télécharger le Plugin',
    printPdf: 'Imprimer / Sauvegarder PDF',
    close: 'Fermer',
    requirements: {
      title: 'Prérequis',
      items: [
        { label: 'WordPress', value: '5.0 ou supérieur (6.0+ recommandé)' },
        { label: 'WooCommerce', value: '5.0 ou supérieur (7.0+ recommandé)' },
        { label: 'PHP', value: '7.4 ou supérieur (8.0+ recommandé)' },
        { label: 'Certificat SSL', value: 'Requis (HTTPS)' }
      ]
    },
    steps: [
      {
        number: 1,
        title: 'Télécharger le Plugin',
        description: 'Téléchargez le fichier du plugin PayHub Gateway (payhub-gateway.zip) depuis votre tableau de bord admin.',
        tips: ['Gardez le fichier ZIP tel quel, ne l\'extrayez pas', 'Le fichier fait environ 12 Ko']
      },
      {
        number: 2,
        title: 'Téléverser sur WordPress',
        description: 'Dans votre panneau d\'administration WordPress, allez dans Extensions → Ajouter → Téléverser une extension.',
        substeps: [
          'Connectez-vous à votre admin WordPress (votresite.com/wp-admin)',
          'Allez dans Extensions → Ajouter',
          'Cliquez sur le bouton "Téléverser une extension" en haut',
          'Cliquez sur "Choisir un fichier" et sélectionnez le ZIP',
          'Cliquez sur "Installer maintenant"'
        ]
      },
      {
        number: 3,
        title: 'Activer le Plugin',
        description: 'Après l\'installation, cliquez sur "Activer l\'extension" pour activer PayHub Gateway.',
        tips: ['Un message de succès s\'affichera une fois activé', 'Le plugin apparaîtra dans votre liste d\'extensions']
      },
      {
        number: 4,
        title: 'Configurer WooCommerce',
        description: 'Allez dans WooCommerce → Réglages → Paiements pour configurer la méthode de paiement PayHub.',
        substeps: [
          'Naviguez vers WooCommerce → Réglages',
          'Cliquez sur l\'onglet "Paiements"',
          'Trouvez "PayHub" dans la liste des méthodes de paiement',
          'Cliquez sur "Configurer" ou "Gérer"'
        ]
      },
      {
        number: 5,
        title: 'Entrer Votre Clé API',
        description: 'Entrez votre clé API PayHub pour connecter votre boutique.',
        substeps: [
          'Activez la méthode de paiement en cochant "Activer PayHub"',
          'Entrez un titre (ex: "Payer avec PayHub")',
          'Entrez une description pour les clients',
          'Collez votre Clé API (obtenez-la dans Paramètres → Clés API)',
          'Définissez l\'URL API: ' + API_URL + '/api/woocommerce',
          'Cliquez sur "Enregistrer les modifications"'
        ],
        important: 'Votre clé API peut être créée dans la section Paramètres → Clés API de votre compte PayHub.'
      },
      {
        number: 6,
        title: 'Tester l\'Intégration',
        description: 'Effectuez un achat test pour vous assurer que tout fonctionne correctement.',
        substeps: [
          'Ajoutez un produit à votre panier',
          'Procédez au paiement',
          'Sélectionnez "Payer avec PayHub" comme méthode de paiement',
          'Complétez la commande',
          'Vérifiez que le paiement apparaît dans les transactions PayHub'
        ],
        tips: ['Utilisez votre propre compte pour les tests', 'Vérifiez les tableaux de bord acheteur et vendeur']
      }
    ],
    troubleshooting: {
      title: 'Dépannage',
      items: [
        {
          problem: 'Le plugin n\'apparaît pas dans les méthodes de paiement',
          solution: 'Assurez-vous que WooCommerce est installé et activé. Essayez de désactiver et réactiver le plugin PayHub.'
        },
        {
          problem: 'Erreur de connexion API',
          solution: 'Vérifiez que votre clé API est correcte et non révoquée. Assurez-vous que votre site utilise HTTPS.'
        },
        {
          problem: 'Le paiement échoue lors du checkout',
          solution: 'Vérifiez que le client a un solde suffisant. Vérifiez que l\'URL API est correcte.'
        }
      ]
    },
    support: {
      title: 'Besoin d\'Aide?',
      text: 'Contactez notre équipe de support ou consultez la Base de Connaissances dans votre tableau de bord admin pour plus d\'informations.'
    }
  }
};

// Step Icon mapping
const stepIcons = {
  1: Download,
  2: Upload,
  3: Plug,
  4: Settings,
  5: Key,
  6: ShoppingCart
};

export function WooCommerceGuide({ language = 'fr', isOpen, onClose, adminKey = null }) {
  const content = guideContent[language] || guideContent.fr;
  const printRef = useRef(null);
  const [activeStep, setActiveStep] = useState(1);

  const handleDownloadPlugin = async () => {
    try {
      let headers = {};
      if (adminKey) {
        headers['X-Admin-Key'] = adminKey;
      }
      
      const response = await fetch(`${API_URL}/api/admin/woocommerce-plugin/download`, {
        headers
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'payhub-gateway.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(language === 'fr' ? 'Plugin téléchargé!' : 'Plugin downloaded!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de téléchargement' : 'Download error');
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${content.title}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #1a1a1a;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          h1 { font-size: 24px; margin-bottom: 8px; color: #166534; }
          h2 { font-size: 18px; margin: 24px 0 12px; color: #166534; border-bottom: 2px solid #22c55e; padding-bottom: 4px; }
          h3 { font-size: 16px; margin: 16px 0 8px; }
          p { margin-bottom: 12px; }
          .subtitle { color: #666; margin-bottom: 24px; }
          .requirements { background: #f0fdf4; padding: 16px; border-radius: 8px; margin-bottom: 24px; }
          .requirements table { width: 100%; border-collapse: collapse; }
          .requirements td { padding: 8px 0; border-bottom: 1px solid #dcfce7; }
          .requirements td:first-child { font-weight: 600; width: 40%; }
          .step { margin-bottom: 24px; page-break-inside: avoid; }
          .step-header { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
          .step-number { 
            background: #166534; color: white; width: 32px; height: 32px; 
            border-radius: 50%; display: flex; align-items: center; justify-content: center;
            font-weight: bold; flex-shrink: 0;
          }
          .step-title { font-weight: 600; font-size: 16px; }
          .step-image { width: 100%; max-width: 300px; margin: 16px auto; display: block; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
          .substeps { margin: 12px 0 12px 44px; }
          .substeps li { margin-bottom: 6px; }
          .tips { background: #fef3c7; padding: 12px; border-radius: 6px; margin: 12px 0 12px 44px; font-size: 14px; }
          .tips-title { font-weight: 600; color: #92400e; margin-bottom: 4px; }
          .important { background: #dbeafe; padding: 12px; border-radius: 6px; margin: 12px 0 12px 44px; font-size: 14px; }
          .important-title { font-weight: 600; color: #1e40af; margin-bottom: 4px; }
          .troubleshooting { margin-top: 32px; }
          .trouble-item { margin-bottom: 16px; padding-left: 16px; border-left: 3px solid #f59e0b; }
          .trouble-problem { font-weight: 600; color: #92400e; }
          .trouble-solution { color: #666; }
          .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e5e5; color: #666; font-size: 14px; text-align: center; }
          .api-url { background: #f3f4f6; padding: 8px 12px; border-radius: 4px; font-family: monospace; font-size: 13px; word-break: break-all; }
          @media print {
            body { padding: 20px; }
            .step { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>🟢 ${content.title}</h1>
        <p class="subtitle">${content.subtitle}</p>
        
        <div class="requirements">
          <h3>${content.requirements.title}</h3>
          <table>
            ${content.requirements.items.map(item => `
              <tr><td>${item.label}</td><td>${item.value}</td></tr>
            `).join('')}
          </table>
        </div>

        ${content.steps.map(step => `
          <div class="step">
            <div class="step-header">
              <div class="step-number">${step.number}</div>
              <div class="step-title">${step.title}</div>
            </div>
            <img src="${stepImages[step.number]}" alt="${step.title}" class="step-image" />
            <p style="margin-left: 44px;">${step.description}</p>
            ${step.substeps ? `
              <ol class="substeps">
                ${step.substeps.map(sub => `<li>${sub}</li>`).join('')}
              </ol>
            ` : ''}
            ${step.tips ? `
              <div class="tips">
                <div class="tips-title">💡 Tips</div>
                ${step.tips.map(tip => `<div>• ${tip}</div>`).join('')}
              </div>
            ` : ''}
            ${step.important ? `
              <div class="important">
                <div class="important-title">ℹ️ Important</div>
                <div>${step.important}</div>
              </div>
            ` : ''}
          </div>
        `).join('')}

        <div class="troubleshooting">
          <h2>${content.troubleshooting.title}</h2>
          ${content.troubleshooting.items.map(item => `
            <div class="trouble-item">
              <div class="trouble-problem">${item.problem}</div>
              <div class="trouble-solution">${item.solution}</div>
            </div>
          `).join('')}
        </div>

        <div class="footer">
          <p><strong>${content.support.title}</strong></p>
          <p>${content.support.text}</p>
          <p style="margin-top: 16px;">PayHub © ${new Date().getFullYear()}</p>
        </div>
      </body>
      </html>
    `;
    // Render the print document via a Blob URL (avoids document.write / XSS)
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };
    }
  };

  const copyApiUrl = () => {
    navigator.clipboard.writeText(`${API_URL}/api/woocommerce`);
    toast.success(language === 'fr' ? 'URL copiée!' : 'URL copied!');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <DialogTitle className="text-xl">{content.title}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">{content.subtitle}</p>
              </div>
            </div>
          </DialogHeader>
          
          {/* Action Buttons */}
          <div className="flex gap-3 mt-4">
            {adminKey && (
              <Button onClick={handleDownloadPlugin} className="gap-2" data-testid="guide-download-btn">
                <Download className="h-4 w-4" />
                {content.downloadPlugin}
              </Button>
            )}
            <Button variant="outline" onClick={handlePrint} className="gap-2" data-testid="guide-print-btn">
              <Printer className="h-4 w-4" />
              {content.printPdf}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" ref={printRef}>
          {/* Requirements */}
          <Card className="mb-6 border-green-200 bg-green-50/50 dark:bg-green-900/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4 text-green-600" />
                {content.requirements.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {content.requirements.items.map((item) => (
                  <div key={item.label} className="flex justify-between text-sm">
                    <span className="font-medium">{item.label}</span>
                    <span className="text-muted-foreground">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Interactive Steps */}
          <div className="space-y-4">
            {content.steps.map((step) => {
              const StepIcon = stepIcons[step.number] || FileText;
              const isActive = activeStep === step.number;
              const isCompleted = activeStep > step.number;
              
              return (
                <Card 
                  key={step.number}
                  className={`cursor-pointer transition-all ${
                    isActive ? 'ring-2 ring-primary shadow-md' : 
                    isCompleted ? 'bg-green-50/50 dark:bg-green-900/10' : 'hover:shadow-sm'
                  }`}
                  onClick={() => setActiveStep(step.number)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Step Number */}
                      <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center font-bold text-lg ${
                        isCompleted ? 'bg-green-500 text-white' :
                        isActive ? 'bg-primary text-primary-foreground' : 
                        'bg-muted text-muted-foreground'
                      }`}>
                        {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : step.number}
                      </div>
                      
                      {/* Step Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <StepIcon className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                          <h3 className={`font-semibold ${isActive ? 'text-primary' : ''}`}>{step.title}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{step.description}</p>
                        
                        {/* Expanded Content */}
                        {isActive && (
                          <div className="space-y-3 animate-fade-in-up">
                            {/* Step Image */}
                            <div className="flex justify-center my-4">
                              <img 
                                src={stepImages[step.number]} 
                                alt={step.title}
                                className="max-w-[250px] rounded-lg shadow-md border"
                              />
                            </div>
                            
                            {step.substeps && (
                              <ol className="space-y-2 ml-4">
                                {step.substeps.map((sub, i) => (
                                  <li key={`${step.number}-${sub}`} className="text-sm flex items-start gap-2">
                                    <span className="text-primary font-semibold">{i + 1}.</span>
                                    <span>{sub}</span>
                                  </li>
                                ))}
                              </ol>
                            )}
                            
                            {step.tips && (
                              <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                                <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-1">💡 Tips</p>
                                {step.tips.map((tip) => (
                                  <p key={tip} className="text-sm text-amber-600 dark:text-amber-300">• {tip}</p>
                                ))}
                              </div>
                            )}
                            
                            {step.important && (
                              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                <p className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-1">ℹ️ Important</p>
                                <p className="text-sm text-blue-600 dark:text-blue-300">{step.important}</p>
                              </div>
                            )}

                            {/* API URL for step 5 */}
                            {step.number === 5 && (
                              <div className="bg-muted p-3 rounded-lg">
                                <p className="text-sm font-medium mb-2">API URL:</p>
                                <div className="flex items-center gap-2">
                                  <code className="flex-1 text-xs bg-background p-2 rounded border font-mono overflow-x-auto">
                                    {API_URL}/api/woocommerce
                                  </code>
                                  <Button size="sm" variant="outline" onClick={copyApiUrl}>
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Expand Arrow */}
                      <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${isActive ? 'rotate-90' : ''}`} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Troubleshooting */}
          <Card className="mt-6 border-amber-200 bg-amber-50/50 dark:bg-amber-900/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{content.troubleshooting.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {content.troubleshooting.items.map((item) => (
                <div key={item.problem} className="border-l-2 border-amber-400 pl-3">
                  <p className="font-medium text-amber-700 dark:text-amber-400">{item.problem}</p>
                  <p className="text-sm text-muted-foreground">{item.solution}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Support */}
          <div className="mt-6 text-center p-4 bg-muted/50 rounded-lg">
            <p className="font-medium">{content.support.title}</p>
            <p className="text-sm text-muted-foreground">{content.support.text}</p>
          </div>
        </div>

        {/* Navigation Footer */}
        <div className="p-4 border-t bg-muted/30 flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
            disabled={activeStep === 1}
          >
            {language === 'fr' ? '← Précédent' : '← Previous'}
          </Button>
          <span className="text-sm text-muted-foreground">
            {language === 'fr' ? 'Étape' : 'Step'} {activeStep} / {content.steps.length}
          </span>
          <Button 
            onClick={() => setActiveStep(Math.min(content.steps.length, activeStep + 1))}
            disabled={activeStep === content.steps.length}
          >
            {language === 'fr' ? 'Suivant →' : 'Next →'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Simple button to open the guide
export function WooCommerceGuideButton({ language = 'fr', adminKey = null, variant = 'default', size = 'default', className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <Button 
        variant={variant} 
        size={size} 
        className={className}
        onClick={() => setIsOpen(true)}
        data-testid="open-woocommerce-guide-btn"
      >
        <FileText className="h-4 w-4 mr-2" />
        {language === 'fr' ? 'Guide d\'Installation' : 'Installation Guide'}
      </Button>
      <WooCommerceGuide 
        language={language} 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)}
        adminKey={adminKey}
      />
    </>
  );
}
